import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  MenuItem,
  Typography,
} from "@mui/material";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { useRoutes } from "@hooks";
import { Account } from "@bn/api/apiv1";
import UpdateIcon from "@icons/myicons/refresh.svg?react";
import AddIcon from "@icons/myicons/plus.svg?react";
import EditIcon from "@icons/myicons/edit.svg?react";
import UploadIcon from "@icons/myicons/cloud-storage-upload.svg?react";
import ManageBankConnectionIcon from "@icons/myicons/bank-edit.svg?react";
import HideIcon from "@icons/myicons/eye-cross.svg?react";
import UnhideIcon from "@icons/myicons/eye.svg?react";
import CancelIcon from "@icons/myicons/delete-circle.svg?react";
import { MultibankingApiProvider, useMyself } from "@api";

interface Props {
  account: Account;
  handleOpenDeleteModal: () => void;
  handleHideAccount: () => void;
  ref?: React.Ref<HTMLDivElement>;
}

export const SingleAccountMenu = (props: Props) => {
  const { account, handleOpenDeleteModal, handleHideAccount, ref } = props;
  const { pushUpdateConnection } = useRoutes();
  const { data: myself } = useMyself();
  const isQA = myself?.roles?.includes("ROLE_QUALITY_ASSURANCE");

  const { t } = useTranslation();
  const shouldDisplayUpdateButton =
    Boolean(account?.bankConnectionType) &&
    Boolean(account?.bankConnectionId) &&
    account?.bankConnectionType !== "NETFONDS";

  const [isUpdating, setIsUpdating] = useState(false);

  const handleReconnect = async () => {};
  const updateAccount = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!shouldDisplayUpdateButton || isUpdating) return;
    setIsUpdating(true);
    try {
      await pushUpdateConnection(
        account?.bankConnectionId!,
        account?.bankConnectionType as MultibankingApiProvider
      );
    } finally {
      setIsUpdating(false);
    }
  };

  if (!account) return null;

  return (
    <div ref={ref}>
      {shouldDisplayUpdateButton && (
        <MenuItem
          data-testid="account-update"
          onClick={updateAccount}
          disabled={isUpdating}
        >
          <Box display="flex" alignItems="center">
            {isUpdating ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <UpdateIcon />
            )}
            <Typography style={{ marginLeft: "12px" }} variant="inherit">
              {t("singleAccountPage.header.update")}
            </Typography>
          </Box>
        </MenuItem>
      )}
      {/*@ts-ignore*/}
      {isQA && account?.bankConnectionStatus?.status === "INACTIVE" && (
        <MenuItem data-testid="depot-reconnect" onClick={handleReconnect}>
          <Box display="flex" alignItems="center">
            <UpdateIcon />
            <Typography style={{ marginLeft: "12px" }} variant="inherit">
              {t("depotPage.headerMenu.reconnect")}
            </Typography>
          </Box>
        </MenuItem>
      )}
      <MenuItem
        data-testid="csv-upload"
        component={Link}
        to={`/depots/${account?.id}/csv-drop`}
      >
        <Box display="flex" alignItems="center">
          <UploadIcon />
          <Typography style={{ marginLeft: "12px" }} variant="inherit">
            {t("depotPage.headerMenu.uploadCsv")}
          </Typography>
        </Box>
      </MenuItem>
      <MenuItem
        data-testid="add-cash-transaction"
        component={Link}
        to={`/accounts/${account?.id}/transactions/add-manually`}
      >
        <Box display="flex" alignItems="center">
          <AddIcon width="24px" height="14px" />
          <Typography style={{ marginLeft: "12px" }} variant="inherit">
            {t("singleAccountPage.header.addTransaction")}
          </Typography>
        </Box>
      </MenuItem>
      <MenuItem
        data-testid="edit"
        component={Link}
        to={`/accounts/${account?.id}/edit`}
      >
        <Box display="flex" alignItems="center">
          <EditIcon />
          <Typography style={{ marginLeft: "12px" }} variant="inherit">
            {t("singleAccountPage.header.edit")}
          </Typography>
        </Box>
      </MenuItem>
      {Boolean(account?.bankConnectionType) && (
        <MenuItem
          data-testid="manage-bank-connection"
          component={Link}
          to={`/settings/bankConnections/${account?.bankConnectionType}/${account?.bankConnectionId}`}
        >
          <Box display="flex" alignItems="center">
            <ManageBankConnectionIcon />
            <Typography style={{ marginLeft: "12px" }} variant="inherit">
              {t("singleAccountPage.header.manageBankConnection")}
            </Typography>
          </Box>
        </MenuItem>
      )}
      <Divider />
      <MenuItem
        onClick={handleHideAccount}
        data-testid={account?.isHidden ? "unhide-account" : "hide-account"}
      >
        <Box display="flex" alignItems="center">
          {account?.isHidden ? <UnhideIcon /> : <HideIcon />}
          <Typography style={{ marginLeft: "12px" }} variant="inherit">
            {account?.isHidden
              ? t("singleAccountPage.header.show")
              : t("singleAccountPage.header.hide")}
          </Typography>
        </Box>
      </MenuItem>
      <Divider />
      <MenuItem
        data-testid="delete-account-item"
        onClick={handleOpenDeleteModal}
      >
        <Box display="flex" alignItems="center" color="error.main">
          <CancelIcon />
          <Typography style={{ marginLeft: "12px" }} variant="inherit">
            {t("singleAccountPage.header.delete")}
          </Typography>
        </Box>
      </MenuItem>
    </div>
  );
};
